(() => {
  const root = document.querySelector('[data-rsp-match-workspace]');
  if (!root) return;

  const form = root.querySelector('[data-rsp-match-form]');
  const fields = [...form.querySelectorAll('input[name], select[name]')].filter((field) => !['q', 'type', 'vin'].includes(field.name));
  const vinField = form.querySelector('[data-rsp-vin]');
  const vinButton = form.querySelector('[data-rsp-vin-decode]');
  const vinResult = form.querySelector('[data-rsp-vin-result]');
  const checks = [...root.querySelectorAll('[data-rsp-match-check]')];
  const notes = [...root.querySelectorAll('[data-rsp-match-note]')];
  const progress = root.querySelector('[data-rsp-match-progress]');
  const error = root.querySelector('[data-rsp-match-error]');
  const storageKey = 'roadspec-part-match-workspace-v1';

  const readSaved = () => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '{}'); } catch (_) { return {}; }
  };

  const save = () => {
    const state = {
      fields: Object.fromEntries(fields.map((field) => [field.name, field.value.trim()])),
      checks: checks.map((field) => field.checked),
      notes: notes.map((field) => field.value.trim()),
    };
    try { localStorage.setItem(storageKey, JSON.stringify(state)); } catch (_) {}
    updateProgress();
  };

  const updateProgress = () => {
    const completed = checks.filter((field, index) => field.checked || notes[index]?.value.trim()).length;
    progress.textContent = `${completed} of ${checks.length} details recorded`;
  };

  const restore = () => {
    const state = readSaved();
    fields.forEach((field) => { if (state.fields?.[field.name]) field.value = state.fields[field.name]; });
    checks.forEach((field, index) => { field.checked = Boolean(state.checks?.[index]); });
    notes.forEach((field, index) => { if (state.notes?.[index]) field.value = state.notes[index]; });

    const params = new URLSearchParams(window.location.search);
    fields.forEach((field) => {
      const incoming = params.get(field.name);
      if (incoming) field.value = incoming.slice(0, 240);
    });
    save();
  };

  const publishEvent = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(name, { detail }));
    window.dataLayer?.push({ event: name, ...detail });
  };

  const setVinMessage = (message, state = 'info') => {
    if (!vinResult) return;
    vinResult.hidden = false;
    vinResult.dataset.state = state;
    vinResult.innerHTML = message;
  };

  const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));

  const normalizeVin = () => (vinField?.value || '').toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '').slice(0, 17);

  const addOrSelectMake = (make) => {
    const makeField = form.elements.make;
    if (!makeField || !make) return;
    const match = [...makeField.options].find((option) => option.value.toLowerCase() === make.toLowerCase());
    if (match) {
      makeField.value = match.value;
      return;
    }
    makeField.add(new Option(make, make));
    makeField.value = make;
  };

  const decodeVin = async () => {
    const vin = normalizeVin();
    if (vinField) vinField.value = vin;
    if (vin.length !== 17) {
      setVinMessage('Enter a complete 17-character VIN. The letters I, O and Q are not used in standard VINs.', 'error');
      vinField?.focus();
      return;
    }
    if (vinButton) {
      vinButton.disabled = true;
      vinButton.textContent = 'Decoding…';
    }
    setVinMessage('Checking the vehicle record…', 'loading');
    try {
      const response = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValuesExtended/${encodeURIComponent(vin)}?format=json`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('VIN service unavailable');
      const payload = await response.json();
      const vehicle = payload?.Results?.[0];
      if (!vehicle) throw new Error('No vehicle record returned');
      const errorCode = String(vehicle.ErrorCode || '').split(',')[0].trim();
      if (errorCode && errorCode !== '0') {
        const errorText = [vehicle.ErrorText, vehicle.AdditionalErrorText].filter(Boolean).join(' ');
        throw new Error(errorText || 'The VIN could not be decoded.');
      }
      const year = vehicle.ModelYear || '';
      const make = vehicle.Make || '';
      const model = vehicle.Model || '';
      if (year) form.elements.year.value = year;
      if (make) addOrSelectMake(make);
      if (model) form.elements.model.value = model;
      save();
      const trim = vehicle.Trim || vehicle.Series || '';
      const vehicleLabel = [year, make, model, trim].filter(Boolean).join(' ');
      if (!vehicleLabel) throw new Error('The decoder returned no usable vehicle details.');
      setVinMessage(`<strong>${escapeHtml(vehicleLabel)}</strong><span>Vehicle details added below. Confirm the OEM/reference number, options, connectors and position before ordering; a VIN decode does not guarantee part fitment.</span>`, 'success');
      publishEvent('roadspec_vin_decode', { success: true, has_trim: Boolean(trim) });
    } catch (decodeError) {
      setVinMessage(`${escapeHtml(decodeError.message || 'We could not decode that VIN.')} <a href="/pages/contact">Ask our team to help</a>.`, 'error');
      publishEvent('roadspec_vin_decode', { success: false });
    } finally {
      if (vinButton) {
        vinButton.disabled = false;
        vinButton.textContent = 'Decode VIN';
      }
    }
  };

  [...fields, ...checks, ...notes].forEach((field) => {
    field.addEventListener(field.type === 'checkbox' || field.tagName === 'SELECT' ? 'change' : 'input', () => {
      error.hidden = true;
      save();
    });
  });

  vinField?.addEventListener('input', () => {
    vinField.value = normalizeVin();
    if (vinResult) vinResult.hidden = true;
  });
  vinButton?.addEventListener('click', decodeVin);
  vinField?.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      decodeVin();
    }
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const values = Object.fromEntries(fields.map((field) => [field.name, field.value.trim()]));
    const query = [values.year, values.make, values.model, values.part, values.oem, values.position, values.sku]
      .filter(Boolean)
      .join(' ')
      .replace(/https?:\/\/[^\s]+\/products\//i, '')
      .replace(/[-_]+/g, ' ')
      .trim();
    if (!query) {
      error.hidden = false;
      fields[0]?.focus();
      return;
    }
    form.querySelector('[data-rsp-match-query]').value = query;
    publishEvent('roadspec_part_match_search', { detail_count: Object.values(values).filter(Boolean).length });
    window.location.assign(`${form.action}?type=product&q=${encodeURIComponent(query)}`);
  });

  root.querySelector('[data-rsp-match-print]')?.addEventListener('click', () => {
    publishEvent('roadspec_part_match_print', { completed_count: checks.filter((field) => field.checked).length });
    window.print();
  });

  root.querySelector('[data-rsp-match-clear]')?.addEventListener('click', () => {
    fields.forEach((field) => { field.value = ''; });
    checks.forEach((field) => { field.checked = false; });
    notes.forEach((field) => { field.value = ''; });
    try { localStorage.removeItem(storageKey); } catch (_) {}
    updateProgress();
    fields[0]?.focus();
    publishEvent('roadspec_part_match_clear');
  });

  restore();
})();
